import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  OnboardingState,
  OnboardingStepsUpdate,
  OnboardingActionResponse,
  OnboardingTimeZoneConfirmationResponse,
  SelectModulesPayload,
  SelectModulesResponse,
} from '@/lib/types';

export function useOnboardingState(options: { refetchOnMount?: 'always' } = {}) {
  return useQuery({
    ...options,
    queryKey: queryKeys.onboarding.state(),
    queryFn: async (): Promise<OnboardingState> => {
      const response = await apiClient.get<OnboardingState>('/api/v1/onboarding/');
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateOnboardingSteps() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: OnboardingStepsUpdate): Promise<OnboardingState> => {
      const response = await apiClient.patch<OnboardingState>(
        '/api/v1/onboarding/steps/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
  });
}

export function useRecordFirstLoginRedirect() {
  const queryClient = useQueryClient();

  return useMutation({
    // ``storageKey`` is the browser flag set when the redirect fired.  It is
    // cleared in the hook-level onError, which runs even after the caller
    // unmounts (the redirect navigates away immediately), so a failed save
    // is retried on a later visit.
    mutationFn: async (
      _vars: { storageKey?: string } = {},
    ): Promise<{ first_login_redirect_done: boolean }> => {
      const response = await apiClient.post<{ first_login_redirect_done: boolean }>(
        '/api/v1/onboarding/first-login-redirect/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
    onError: (_error, vars) => {
      if (!vars?.storageKey) return;
      try {
        localStorage.removeItem(vars.storageKey);
      } catch {
        // Storage unavailable — nothing to clear.
      }
    },
  });
}

export function useConfirmOnboardingTimeZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timeZone: string): Promise<OnboardingTimeZoneConfirmationResponse> => {
      const response = await apiClient.patch<OnboardingTimeZoneConfirmationResponse>(
        '/api/v1/onboarding/time-zone/',
        { time_zone: timeZone }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<OnboardingActionResponse> => {
      const response = await apiClient.post<OnboardingActionResponse>(
        '/api/v1/onboarding/complete/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
    },
  });
}

export function useSkipOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<OnboardingActionResponse> => {
      const response = await apiClient.post<OnboardingActionResponse>(
        '/api/v1/onboarding/skip/'
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.organization() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.modules() });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useSelectModules() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SelectModulesPayload): Promise<SelectModulesResponse> => {
      const response = await apiClient.post<SelectModulesResponse>(
        '/api/v1/onboarding/select-modules/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.onboarding.state() });
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.modules() });
    },
  });
}
